import { useState } from 'react';
import type { AssetImpact } from '../types';
import { assetImpactBlockReason } from '../plant/publicationBlockReasons';

/**
 * The asset fields worth reading on a publish: what the asset is called, what it
 * is, where it sits in the taxonomy the tree view groups by, how it reads to a
 * person, and the customer's own class for it. The row header shows the new tag,
 * so a rename is only legible as an old -> new line. tag_status is machinery.
 */
const IMPACT_FIELD_LABELS: Record<string, string> = {
  tag: 'tag',
  asset_class: 'class',
  asset_type: 'group',
  display_name: 'description',
  primary: 'primary P&ID',
  equipment_category: 'customer category',
  equipment_class: 'customer class',
  equipment_class_description: 'customer class description',
};

const IMPACT_ROW_CAP = 15;
const SKIP_COLLAPSE_THRESHOLD = 5;

const isSkip = (impact: AssetImpact): boolean => impact.action.startsWith('skip_');

const formatImpactValue = (value: string | null): string => (value ? value : '—');

const skipMessage = (impact: AssetImpact, currentDrawingNo: string | null): string => {
  const blockReason = assetImpactBlockReason(impact.action);
  if (blockReason) {
    return blockReason;
  }
  if (impact.action === 'skip_empty_tag') {
    return `Tag '${impact.tag}' cannot identify an asset — no register writes.`;
  }
  if (impact.action === 'skip_class_conflict') {
    const reclassification = impact.changes.find((change) => change.field === 'asset_class');
    return reclassification?.old && reclassification.new
      ? `Register lists this as ${reclassification.old}; drawing says ${reclassification.new} — reclassification needs review.`
      : 'Register lists this as a different asset class — reclassification needs review.';
  }
  const drawings = [currentDrawingNo, ...impact.files.map((file) => file.drawing_no)].filter(
    (drawingNo): drawingNo is string => Boolean(drawingNo),
  );
  const uniqueDrawings = Array.from(new Set(drawings));
  const instruction = 'No primary P&ID marked — tick it to publish this asset';
  return uniqueDrawings.length > 1
    ? `${instruction}. Drawn on ${uniqueDrawings.join(', ')}`
    : instruction;
};

const owningDrawing = (impact: AssetImpact): string | null => {
  const owner = impact.files.find((file) => file.is_primary) ?? impact.files[0];
  return owner?.drawing_no ?? null;
};

const drawingLabel = (file: AssetImpact['files'][number]): string =>
  file.drawing_no || file.source_file_id;

function AppearanceDrawings({ files }: { files: AssetImpact['files'] }) {
  if (files.length === 0) return null;
  const ordered = [...files].sort(
    (left, right) => Number(Boolean(right.is_primary)) - Number(Boolean(left.is_primary)),
  );
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {ordered.map((file) => (
        <span
          key={file.source_file_id}
          style={{
            borderRadius: 999,
            padding: '1px 8px',
            fontSize: 10,
            fontWeight: file.is_primary ? 800 : 600,
            backgroundColor: file.is_primary ? '#eff6ff' : '#f3f4f6',
            border: `1px solid ${file.is_primary ? '#bfdbfe' : '#e5e7eb'}`,
            color: file.is_primary ? '#1d4ed8' : '#4b5563',
          }}
        >
          {drawingLabel(file)}
          {file.is_primary ? ' · primary' : ''}
        </span>
      ))}
    </div>
  );
}

const linkButtonStyle: React.CSSProperties = {
  border: 'none',
  background: 'none',
  padding: 0,
  fontSize: 11,
  fontWeight: 700,
  color: '#2563eb',
  cursor: 'pointer',
};

const sectionLabelStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 800,
  letterSpacing: 0.6,
  color: '#6b7280',
  marginBottom: 6,
};

/**
 * What a publish would do to the plant register, asset by asset: the fields
 * each write moves, then the tags publishing deliberately leaves alone.
 */
export const PublicationRegisterImpact: React.FC<{
  impacts: AssetImpact[];
  note?: string;
  hierarchyNote?: string;
  currentDrawingNo: string | null;
  /** Plant plans list every sheet a tag lands on; a single-drawing publish does not. */
  showAppearanceDrawings?: boolean;
}> = ({ impacts, note, hierarchyNote, currentDrawingNo, showAppearanceDrawings = false }) => {
  const [showAllWrites, setShowAllWrites] = useState(false);
  const [showUnchanged, setShowUnchanged] = useState(false);
  const [showEvidenceOnly, setShowEvidenceOnly] = useState(false);
  const [showSkips, setShowSkips] = useState(false);

  if (note) {
    return <div style={{ color: '#6b7280' }}>{note}</div>;
  }
  const groupingWarning = hierarchyNote ? (
    <div
      style={{
        padding: '6px 8px',
        borderRadius: 6,
        backgroundColor: '#fffbeb',
        border: '1px solid #fde68a',
        color: '#92400e',
      }}>
      {hierarchyNote}
    </div>
  ) : null;

  if (impacts.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {groupingWarning}
        <div style={{ color: '#6b7280' }}>No plant-register writes.</div>
      </div>
    );
  }

  const writes = impacts.filter(
    (impact) => impact.action === 'create' || impact.action === 'update',
  );
  const unchanged = impacts.filter((impact) => impact.action === 'unchanged');
  const evidenceOnly = impacts.filter((impact) => impact.action === 'evidence_only');
  const skips = impacts.filter(isSkip);

  const visibleWrites = showAllWrites ? writes : writes.slice(0, IMPACT_ROW_CAP);
  const hiddenWriteCount = writes.length - visibleWrites.length;
  const skipsCollapsed = skips.length > SKIP_COLLAPSE_THRESHOLD && !showSkips;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {groupingWarning}
      {visibleWrites.length > 0 && (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
          {visibleWrites.map((impact, index) => (
            <div
              key={`${impact.tag}-${index}`}
              style={{
                display: 'flex',
                gap: 10,
                padding: '7px 10px',
                borderTop: index === 0 ? 'none' : '1px solid #f3f4f6',
                alignItems: 'baseline',
              }}>
              <span
                style={{
                  fontFamily: 'monospace',
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#111827',
                  whiteSpace: 'nowrap',
                }}>
                {impact.tag}
              </span>
              <div
                style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                {impact.action === 'create' && (
                  <span
                    style={{
                      alignSelf: 'flex-start',
                      borderRadius: 999,
                      backgroundColor: '#ecfdf3',
                      border: '1px solid #a7f3d0',
                      color: '#047857',
                      padding: '1px 8px',
                      fontSize: 10,
                      fontWeight: 700,
                    }}>
                    new asset
                  </span>
                )}
                {impact.changes
                  .filter((change) => change.field in IMPACT_FIELD_LABELS)
                  .filter((change) => change.field !== 'tag' || change.old !== null)
                  .map((change) => (
                    <div
                      key={change.field}
                      style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: 0.4,
                          color: '#9ca3af',
                          whiteSpace: 'nowrap',
                        }}>
                        {IMPACT_FIELD_LABELS[change.field]}
                      </span>
                      {impact.action === 'update' && (
                        <>
                          <span style={{ color: '#6b7280' }}>{formatImpactValue(change.old)}</span>
                          <span style={{ color: '#9ca3af' }}>→</span>
                        </>
                      )}
                      <span style={{ color: '#111827', fontWeight: 600 }}>
                        {formatImpactValue(change.new)}
                      </span>
                    </div>
                  ))}
                {showAppearanceDrawings && <AppearanceDrawings files={impact.files} />}
              </div>
            </div>
          ))}
        </div>
      )}

      {hiddenWriteCount > 0 && (
        <button
          type="button"
          onClick={() => setShowAllWrites(true)}
          style={{ ...linkButtonStyle, marginTop: 6 }}>
          +{hiddenWriteCount} more
        </button>
      )}

      {unchanged.length > 0 && (
        <div style={{ marginTop: 10 }}>
          {showUnchanged ? (
            <div
              style={{
                border: '1px solid #f3f4f6',
                borderRadius: 8,
                overflow: 'hidden',
                backgroundColor: '#fafafa',
              }}>
              {unchanged.map((impact, index) => (
                <div
                  key={`${impact.tag}-${index}`}
                  style={{
                    display: 'flex',
                    gap: 10,
                    padding: '5px 10px',
                    borderTop: index === 0 ? 'none' : '1px solid #f3f4f6',
                    alignItems: 'baseline',
                    color: '#9ca3af',
                  }}>
                  <span
                    style={{
                      fontFamily: 'monospace',
                      fontSize: 11,
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}>
                    {impact.tag}
                  </span>
                  <span style={{ fontSize: 11, flex: 1 }}>unchanged</span>
                  {showAppearanceDrawings && <AppearanceDrawings files={impact.files} />}
                </div>
              ))}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowUnchanged(true)}
              style={{ ...linkButtonStyle, color: '#6b7280', fontVariantNumeric: 'tabular-nums' }}>
              {unchanged.length} asset{unchanged.length === 1 ? '' : 's'} unchanged
            </button>
          )}
        </div>
      )}

      {evidenceOnly.length > 0 && (
        <div style={{ marginTop: 10 }}>
          {showEvidenceOnly ? (
            <div
              style={{
                border: '1px solid #f3f4f6',
                borderRadius: 8,
                overflow: 'hidden',
                backgroundColor: '#fafafa',
              }}>
              {evidenceOnly.map((impact, index) => {
                const owner = owningDrawing(impact);
                return (
                  <div
                    key={`${impact.tag}-${index}`}
                    style={{
                      display: 'flex',
                      gap: 10,
                      padding: '5px 10px',
                      borderTop: index === 0 ? 'none' : '1px solid #f3f4f6',
                      alignItems: 'baseline',
                      color: '#9ca3af',
                    }}>
                    <span
                      style={{
                        fontFamily: 'monospace',
                        fontSize: 11,
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}>
                      {impact.tag}
                    </span>
                    <span style={{ fontSize: 11, flex: 1 }}>
                      {owner ? `primary: ${owner}` : 'primary on another drawing'}
                    </span>
                    {showAppearanceDrawings && <AppearanceDrawings files={impact.files} />}
                  </div>
                );
              })}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowEvidenceOnly(true)}
              style={{ ...linkButtonStyle, color: '#6b7280', fontVariantNumeric: 'tabular-nums' }}>
              {evidenceOnly.length} recorded as secondary appearance
            </button>
          )}
        </div>
      )}

      {skips.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ ...sectionLabelStyle, color: '#b45309' }}>NOT UPDATED</div>
          {skipsCollapsed ? (
            <button type="button" onClick={() => setShowSkips(true)} style={linkButtonStyle}>
              Show {skips.length} tags
            </button>
          ) : (
            <div
              style={{
                border: '1px solid #fde68a',
                borderRadius: 8,
                overflow: 'hidden',
                backgroundColor: '#fffbeb',
              }}>
              {skips.map((impact, index) => (
                <div
                  key={`${impact.tag}-${index}`}
                  style={{
                    display: 'flex',
                    gap: 10,
                    padding: '7px 10px',
                    borderTop: index === 0 ? 'none' : '1px solid #fde68a',
                    alignItems: 'baseline',
                  }}>
                  <span
                    style={{
                      fontFamily: 'monospace',
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#92400e',
                      whiteSpace: 'nowrap',
                    }}>
                    {impact.tag}
                  </span>
                  <span style={{ color: '#b45309', flex: 1, minWidth: 0 }}>
                    {skipMessage(impact, currentDrawingNo)}
                  </span>
                  {showAppearanceDrawings && <AppearanceDrawings files={impact.files} />}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
